import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { defineStore } from 'pinia';
import { useViewerItemStore } from './useViewerItemStore';

export enum ItemTranscriptionStatus {
  PENDING = 'pending',
  WORKING = 'working',
  DONE = 'done',
  FAILED = 'failed',
  EDITED = 'edited',
}

export interface ItemTranscription {
  start: number;
  end: number;
  words: string;
  speaker?: string;
}

// Extra fields added just for editing
export interface ItemTranscriptionEdit extends ItemTranscription {
  deleted?: boolean;
  newStart?: number;
  newEnd?: number;
  newWords?: string;
}

export interface ItemTranscriptionState {
  transcription: {
    id?: string;
    data?: ItemTranscriptionEdit[];
    status?: ItemTranscriptionStatus;
    transcript?: string;
  };
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_TRANSCRIPTION);

export const useTranscriptionStore = defineStore('transcriptionStore', {
  state: () =>
    ({
      transcription: {
        id: undefined,
        data: [],
        status: undefined,
        transcript: undefined,
      },
    }) as ItemTranscriptionState,
  actions: {
    // Initialize viewer from application store initializer
    initialize(data: any) {
      this.setTranscriptionFromRelationship(data.item);

      debug('[initialize] Initialize transcription store', data);
    },
    async loadTranscription(transcriptionId?: string) {
      if (!transcriptionId) {
        return Promise.resolve();
      }

      try {
        const data = await Api.item.transcription.list(transcriptionId);
        this.populateTranscriptionData(data);
        return Promise.resolve();
      } catch (e) {
        debug('Cannot load transcription data', e);
        return Promise.reject('Incorrect transcription data');
      }
    },
    populateTranscriptionData(data: any) {
      if (!data || !data.transcription || !data.transcription.attributes) {
        debug('Cannot load transcription data', data);
        return;
      }

      this.$patch({
        transcription: {
          id: data.transcription.id,
          ...data.transcription.attributes,
        },
      });
    },
    requestTranscriptions(force?: boolean) {
      const itemStore = useViewerItemStore();
      if (!itemStore.id) {
        return Promise.resolve();
      }

      return Api.item.transcription
        .create(itemStore.id, force)
        .then(({ data }) => {
          this.$patch({
            transcription: {
              id: data.transcription.id,
              ...data.transcription.attributes,
            },
          });
          // transcription status is not updated immediately - for smooth exp we set it to WORKING
          this.setTranscriptionStatus(ItemTranscriptionStatus.WORKING);
        })
        .catch(e => {
          if (e?.httpStatus === 409) {
            this.setTranscriptionStatus(ItemTranscriptionStatus.WORKING);
            return;
          }

          this.setTranscriptionStatus(ItemTranscriptionStatus.FAILED);
          throw e;
        });
    },
    setTranscriptionStatus(status: ItemTranscriptionStatus) {
      if (!this.transcription) {
        return;
      }

      this.$patch({ transcription: { status } });
    },
    // this is method to load transcription only from item relationship
    setTranscriptionFromRelationship(data: any) {
      if (!data.relationships?.transcription?.data || !data.relationships.transcription.data?.id) {
        return;
      }

      const id = data.relationships.transcription.data.id;
      const status = data.relationships.transcription?.meta?.status;

      this.$patch({ transcription: { id, status } });
      delete data.relationships.transcription;
    },
    lineDelete(transcription: ItemTranscriptionEdit) {
      if (!this.transcription.data) {
        debug('Missing transcription data', transcription);
        return;
      }
      const index = this.transcription.data.findIndex(t => t === transcription);
      if (index === -1) {
        debug('Cannot delete transcription', transcription);
        return;
      }

      this.transcription.data[index].deleted = true;
    },
    lineUpdate(transcription: ItemTranscriptionEdit, payload: { start?: number; end?: number; words?: string }) {
      if (!this.transcription.data) {
        debug('Missing transcription data', transcription);
        return;
      }
      const index = this.transcription.data.findIndex(t => t === transcription);
      if (index === -1) {
        debug('Cannot update transcription', transcription);
        return;
      }

      if (payload.start) this.transcription.data[index].newStart = payload.start;
      if (payload.end) this.transcription.data[index].newEnd = payload.end;
      if (payload.words) this.transcription.data[index].newWords = payload.words;
    },
    lineRestore(transcription: ItemTranscriptionEdit) {
      if (!this.transcription.data) {
        debug('Missing transcription data', transcription);
        return;
      }
      const index = this.transcription.data.findIndex(t => t === transcription);
      if (index === -1) {
        debug('Cannot restore transcription', transcription);
        return;
      }
      this.transcription.data[index].deleted = false;
      this.transcription.data[index].newStart = undefined;
      this.transcription.data[index].newEnd = undefined;
      this.transcription.data[index].newWords = undefined;
    },
    lineRestoreAll() {
      if (!this.transcription.data) {
        debug('Missing transcription data');
        return;
      }

      this.transcription.data.forEach(t => {
        t.deleted = false;
        t.newStart = undefined;
        t.newEnd = undefined;
        t.newWords = undefined;
      });
    },
    /**
     * Transcription update all lines
     * @returns
     */
    async lineSaveAll() {
      if (!this.transcription.id) {
        debug('Missing transcription id');
        return;
      }
      if (!this.transcription.data) {
        debug('Missing transcription data');
        return;
      }

      const payload = this.transcription.data
        .filter(t => !t.deleted)
        .map(t => {
          return {
            start: t.newStart || t.start,
            end: t.newEnd || t.end,
            words: t.newWords || t.words,
            ...(t.speaker ? { speaker: t.speaker } : {}),
          };
        });

      try {
        const data = await Api.item.transcription.update(this.transcription.id, { data: payload });

        if (!data.transcription || !data.transcription.attributes) {
          debug('Cannot update transcription', data);
          return Promise.reject('Incorrect transcription data');
        }

        this.$patch({
          transcription: {
            id: data.transcription.id,
            ...data.transcription.attributes,
          },
        });
      } catch (e) {
        debug('Cannot update transcription', e);
      }
    },
  },
  getters: {
    isTranscriptionGenerated: (state: ItemTranscriptionState) => !!state.transcription?.id,
    transcriptionId: (state: ItemTranscriptionState) => state.transcription?.id,
    transcriptionStatus: (state: ItemTranscriptionState) => state.transcription?.status,
    isTranscriptionMissingContent: (state: ItemTranscriptionState) => {
      if (state.transcription.status === ItemTranscriptionStatus.DONE || state.transcription.status === ItemTranscriptionStatus.EDITED) {
        return false;
      }
      return !state.transcription.data || state.transcription.data.length === 0;
    },
    isTranscriptionFinished: (state: ItemTranscriptionState) =>
      Boolean(
        state.transcription.status &&
          [ItemTranscriptionStatus.DONE, ItemTranscriptionStatus.EDITED, ItemTranscriptionStatus.FAILED].includes(state.transcription.status)
      ),
    isTranscriptionFailed: (state: ItemTranscriptionState) => state.transcription.status === ItemTranscriptionStatus.FAILED,
    isTranscriptionProcessing: (state: ItemTranscriptionState) =>
      Boolean(state.transcription.status && [ItemTranscriptionStatus.WORKING, ItemTranscriptionStatus.PENDING].includes(state.transcription.status)),
    transcriptionData: (state: ItemTranscriptionState) => state?.transcription.data,
    transcriptionTranscript: (state: ItemTranscriptionState) => state?.transcription.transcript,
    isLineDirty: () => (line: ItemTranscriptionEdit) => line.newWords && line.newWords !== line.words,
  },
});
